import { supabase } from '../lib/supabase';
import { getCurrentOrgId } from '../lib/getCurrentOrgId';

function isSchemaError(error) {
  if (!error) return false;
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  return false;
}

function toCamelCase(obj) {
  if (!obj) return obj;
  const camelObj = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = obj?.[key];
  }
  return camelObj;
}

// Allowed fields for update (whitelist)
const ALLOWED_UPDATE_FIELDS = [
  'company_name', 'trade', 'contact_person', 'contact_email', 'contact_phone',
  'address', 'insurance_expiry', 'rbq_number', 'rbq_expiry',
  'insurance_doc_url', 'rbq_doc_url', 'notes', 'rating', 'status',
  'estimating_contact_name', 'estimating_contact_email', 'estimating_contact_phone', 'tax_id'
];

// Derive file extension from MIME type
function extFromMime(mimeType) {
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType === 'image/jpeg') return 'jpg';
  if (mimeType === 'image/png') return 'png';
  return 'bin';
}

const subcontractorService = {
  async getAll() {
    try {
      const { data, error } = await supabase
        ?.from('subcontractors')
        ?.select('*')
        ?.order('company_name', { ascending: true });

      if (error) {
        if (isSchemaError(error)) { console.error('Schema error:', error?.message); throw error; }
        else if (error?.code === 'PGRST116') return [];
        else { console.log('Data error:', error?.message); return []; }
      }
      return data?.map(toCamelCase) || [];
    } catch (error) {
      console.error('Error fetching subcontractors:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase
        ?.from('subcontractors')
        ?.select('*')
        ?.eq('id', id)
        ?.single();

      if (error) {
        if (isSchemaError(error)) { console.error('Schema error:', error?.message); throw error; }
        else { console.log('Data error:', error?.message); return null; }
      }
      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching subcontractor:', error);
      throw error;
    }
  },

  async create(data) {
    try {
      const orgId = await getCurrentOrgId();
      if (!orgId) throw new Error('No organization found');

      const payload = {
        company_name: data?.companyName || data?.company_name,
        trade: data?.trade,
        contact_person: data?.contactPerson || data?.contact_person || null,
        contact_email: data?.contactEmail || data?.contact_email || data?.email || null,
        contact_phone: data?.contactPhone || data?.contact_phone || data?.phone || null,
        address: data?.address || null,
        notes: data?.notes || null,
        rating: data?.rating || null,
        status: data?.status || 'Active',
        insurance_expiry: data?.insuranceExpiry || data?.insurance_expiry || null,
        rbq_number: data?.rbqNumber || data?.rbq_number || null,
        rbq_expiry: data?.rbqExpiry || data?.rbq_expiry || null,
        insurance_doc_url: data?.insuranceDocUrl || data?.insurance_doc_url || null,
        rbq_doc_url: data?.rbqDocUrl || data?.rbq_doc_url || null,
        estimating_contact_name: data?.estimatingContactName || data?.estimating_contact_name || null,
        estimating_contact_email: data?.estimatingContactEmail || data?.estimating_contact_email || null,
        estimating_contact_phone: data?.estimatingContactPhone || data?.estimating_contact_phone || null,
        tax_id: data?.taxId || data?.tax_id || null,
        organization_id: orgId,
      };

      const { data: created, error } = await supabase
        ?.from('subcontractors')
        ?.insert(payload)
        ?.select()
        ?.single();

      if (error) throw error;
      return toCamelCase(created);
    } catch (error) {
      console.error('Error creating subcontractor:', error);
      throw error;
    }
  },

  async update(id, data) {
    try {
      // Build update payload from whitelisted fields only
      const payload = {};
      const fieldMap = {
        companyName: 'company_name',
        trade: 'trade',
        contactPerson: 'contact_person',
        contactEmail: 'contact_email',
        contactPhone: 'contact_phone',
        email: 'contact_email',
        phone: 'contact_phone',
        address: 'address',
        insuranceExpiry: 'insurance_expiry',
        rbqNumber: 'rbq_number',
        rbqExpiry: 'rbq_expiry',
        insuranceDocUrl: 'insurance_doc_url',
        rbqDocUrl: 'rbq_doc_url',
        notes: 'notes',
        rating: 'rating',
        status: 'status',
        estimatingContactName: 'estimating_contact_name',
        estimatingContactEmail: 'estimating_contact_email',
        estimatingContactPhone: 'estimating_contact_phone',
        taxId: 'tax_id',
      };

      for (const [camelKey, snakeKey] of Object.entries(fieldMap)) {
        if (data?.[camelKey] !== undefined) {
          payload[snakeKey] = data?.[camelKey] === '' ? null : data?.[camelKey];
        }
      }
      // Also accept snake_case keys directly
      for (const snakeKey of ALLOWED_UPDATE_FIELDS) {
        if (data?.[snakeKey] !== undefined && payload?.[snakeKey] === undefined) {
          payload[snakeKey] = data?.[snakeKey] === '' ? null : data?.[snakeKey];
        }
      }

      const { data: updated, error } = await supabase
        ?.from('subcontractors')
        ?.update(payload)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return toCamelCase(updated);
    } catch (error) {
      console.error('Error updating subcontractor:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase
        ?.from('subcontractors')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting subcontractor:', error);
      throw error;
    }
  },

  // ─── Document upload ────────────────────────────────────────────────────────

  async uploadDocument(orgId, subcontractorId, type, file) {
    try {
      // Validate MIME
      const allowedMimes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedMimes?.includes(file?.type)) {
        throw new Error('SVG_NOT_SUPPORTED');
      }
      // Validate size (20 MB)
      if (file?.size > 20 * 1024 * 1024) {
        throw new Error('FILE_TOO_LARGE');
      }

      const ext = extFromMime(file?.type);
      const path = `${orgId}/${subcontractorId}/${type}.${ext}`;

      const { error: uploadError } = await supabase?.storage
        ?.from('subcontractor-documents')
        ?.upload(path, file, { upsert: true, contentType: file?.type });

      if (uploadError) throw uploadError;

      // Update the subcontractor row with the stored path
      const urlColumn = type === 'insurance' ? 'insurance_doc_url' : 'rbq_doc_url';
      const { error: updateError } = await supabase
        ?.from('subcontractors')
        ?.update({ [urlColumn]: path })
        ?.eq('id', subcontractorId);

      if (updateError) throw updateError;

      // Return signed URL (private bucket)
      const signedUrl = await subcontractorService?.getSignedUrl(path);
      return { path, signedUrl };
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },

  async removeDocument(orgId, subcontractorId, type) {
    try {
      // Try to remove all possible extensions (best-effort)
      const exts = ['pdf', 'jpg', 'png'];
      for (const ext of exts) {
        const path = `${orgId}/${subcontractorId}/${type}.${ext}`;
        await supabase?.storage?.from('subcontractor-documents')?.remove([path]);
      }

      // Clear the URL column
      const urlColumn = type === 'insurance' ? 'insurance_doc_url' : 'rbq_doc_url';
      const { error } = await supabase
        ?.from('subcontractors')
        ?.update({ [urlColumn]: null })
        ?.eq('id', subcontractorId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing document:', error);
      throw error;
    }
  },

  async getSignedUrl(path) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('subcontractor-documents')
        ?.createSignedUrl(path, 3600);

      if (error) throw error;
      const ts = Date.now();
      return `${data?.signedUrl}?v=${ts}`;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      return null;
    }
  },

  // ─── Contacts CRUD ──────────────────────────────────────────────────────────

  async listContacts(subcontractorId) {
    try {
      const { data, error } = await supabase
        ?.from('subcontractor_contacts')
        ?.select('*')
        ?.eq('subcontractor_id', subcontractorId)
        ?.order('role', { ascending: true })
        ?.order('name', { ascending: true });

      if (error) throw error;
      return data?.map(toCamelCase) || [];
    } catch (error) {
      console.error('Error listing contacts:', error);
      return [];
    }
  },

  async addContact(subcontractorId, { role, name, email, phone }) {
    try {
      const orgId = await getCurrentOrgId();
      if (!orgId) throw new Error('No organization found');

      const { data, error } = await supabase
        ?.from('subcontractor_contacts')
        ?.insert({
          subcontractor_id: subcontractorId,
          role,
          name,
          email: email || null,
          phone: phone || null,
          organization_id: orgId,
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error adding contact:', error);
      throw error;
    }
  },

  async updateContact(contactId, { role, name, email, phone }) {
    try {
      const { data, error } = await supabase
        ?.from('subcontractor_contacts')
        ?.update({
          role,
          name,
          email: email || null,
          phone: phone || null,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', contactId)
        ?.select()
        ?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating contact:', error);
      throw error;
    }
  },

  async deleteContact(contactId) {
    try {
      const { error } = await supabase
        ?.from('subcontractor_contacts')
        ?.delete()
        ?.eq('id', contactId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting contact:', error);
      throw error;
    }
  },
};

export default subcontractorService;